import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'

import { get, post, put, patch, del } from '@/lib/api'

// ==================== Types ====================

export interface Currency {
    code: string
    name: string
    symbol: string
    decimals: number
    isActive: boolean
    sortOrder: number
    createdAt: string
    updatedAt: string
}

export interface CreateCurrencyInput {
    code: string
    name: string
    symbol: string
    decimals?: number
    sortOrder?: number
}

export interface UpdateCurrencyInput {
    name?: string
    symbol?: string
    decimals?: number
    sortOrder?: number
    isActive?: boolean
}

// ==================== Query Keys ====================

export const currencyKeys = {
    all: ['currencies'] as const,
    lists: () => [...currencyKeys.all, 'list'] as const,
    list: (all?: boolean) => [...currencyKeys.lists(), { all }] as const,
}

// ==================== Hooks ====================

/**
 * 获取启用的货币列表
 */
export function useCurrencies() {
    return useQuery({
        queryKey: currencyKeys.list(false),
        queryFn: () => get<Currency[]>('/finance/currencies'),
        staleTime: 1000 * 60 * 5, // 5 分钟缓存
    })
}

/**
 * 获取所有货币（含禁用）
 */
export function useAllCurrencies() {
    return useQuery({
        queryKey: currencyKeys.list(true),
        queryFn: () => get<Currency[]>('/finance/currencies/all'),
    })
}

/**
 * 创建货币
 */
export function useCreateCurrency() {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: (data: CreateCurrencyInput) => post<Currency>('/finance/currencies', data),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: currencyKeys.all })
        },
    })
}

/**
 * 更新货币
 */
export function useUpdateCurrency() {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: ({ code, data }: { code: string; data: UpdateCurrencyInput }) =>
            put<Currency>(`/finance/currencies/${code}`, data),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: currencyKeys.all })
        },
    })
}

/**
 * 切换货币启用状态
 */
export function useToggleCurrency() {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: (code: string) => patch<Currency>(`/finance/currencies/${code}/toggle`, {}),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: currencyKeys.all })
        },
    })
}

/**
 * 删除货币
 */
export function useDeleteCurrency() {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: (code: string) => del(`/finance/currencies/${code}`),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: currencyKeys.all })
        },
    })
}

/**
 * 初始化默认货币
 */
export function useInitCurrencies() {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: () => post('/finance/currencies/init', {}),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: currencyKeys.all })
        },
    })
}
